import { type SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import { type User } from '../../../../../models/User'
import { getManualParticipantReasonLabel, type ManualParticipant, ManualParticipantAddedReason } from '../../../../../models/ManualParticipant'
import { resolveUserConsent, UserConsentState } from '../../../../../components/Consent/model'
import { getUserDisplayName } from '../../common/aifeatures/utils'
import { type UsersConsent } from '../hooks/users'

export const getManualParticipantReasonOptions = (otherOptionLabel = 'Other'): SelectOption[] => [
  {
    id: ManualParticipantAddedReason.WAS_PRESENT_DID_NOT_SPEAK,
    label: 'Was present but did not speak',
  },
  {
    id: ManualParticipantAddedReason.SPOKE_BUT_WAS_NOT_CAPTURED,
    label: 'Spoke but was not captured',
  },
  {
    id: ManualParticipantAddedReason.WAS_INVITED_BUT_DID_NOT_ATTEND,
    label: 'Was invited but did not attend',
  },
  {
    id: ManualParticipantAddedReason.OTHER,
    label: otherOptionLabel,
  },
]

export const isManualParticipantReasonValid = (reason: ManualParticipantAddedReason | null, otherExplanation: string): boolean => {
  if (!reason) {
    return false
  }

  if (reason !== ManualParticipantAddedReason.OTHER) {
    return true
  }

  return otherExplanation.trim().length > 0
}

export const isManualParticipantConsentProhibitive = (selectedUser: User | null, usersConsent: UsersConsent | null): boolean => {
  if (!selectedUser) {
    return false
  }

  if (selectedUser.type === 'managed') {
    return false
  }

  if (!usersConsent) {
    return resolveUserConsent(selectedUser, null)?.current?.state !== UserConsentState.ACCEPTED
  }

  const selectedUserConsent = resolveUserConsent(selectedUser, usersConsent[selectedUser.id] ?? null)
  if (!selectedUserConsent) {
    return true
  }

  return selectedUserConsent.current?.state !== UserConsentState.ACCEPTED
}

export const didManualParticipantAttend = (reason: ManualParticipantAddedReason | null | undefined): boolean =>
  reason !== ManualParticipantAddedReason.WAS_INVITED_BUT_DID_NOT_ATTEND

export const shouldShowManualParticipantConsentWarning = (
  selectedUser: User | null,
  usersConsent: UsersConsent | null,
  reason: ManualParticipantAddedReason | null,
  consentTrackingEnabled: boolean,
): boolean => {
  if (!consentTrackingEnabled) {
    return false
  }

  return isManualParticipantConsentProhibitive(selectedUser, usersConsent) && didManualParticipantAttend(reason)
}

export const getManualParticipantConsentWarningText = (selectedUser: User | null): string => {
  return selectedUser
    ? `${getUserDisplayName(selectedUser)} has not agreed to this meeting being transcribed and analyzed by your AI Assistant. Meeting will be unavailable by adding this participant, until consent is approved.`
    : ''
}
export const getManualParticipantDisplayReason = (manualParticipant: ManualParticipant, includeZocksDisclaimer: boolean = true): string => {
  const reason = getManualParticipantReasonLabel(manualParticipant.addedReason, manualParticipant.addedReasonExplanation)
  return !manualParticipant.addedReasonConfirmed && includeZocksDisclaimer ? `${reason} (given by Zocks)` : reason
}
